import { useState } from 'react';
import { Tags, Avatar, Text } from '@kula-ai/components';
import type { TagItem } from '@kula-ai/components';
import { CenterSpinner } from '@/components/ats';
import { useGetUserGroup, type UserGroupUser } from '@/api';

const VISIBLE_ROWS = 2;
const ITEMS_PER_ROW = 3;
const INITIAL_VISIBLE = VISIBLE_ROWS * ITEMS_PER_ROW;

const toTagItem = (member: UserGroupUser): TagItem => ({
  id: String(member.id),
  label: member.name,
  leftElement: (
    <Avatar
      src={member.avatar_url}
      name={member.name}
      size="xs"
      shape="square"
    />
  ),
});

interface InterviewPoolListProps {
  value?: number | string | null;
}

/** Renders pool members below the select with show more / show less. */
export const InterviewPoolList = ({ value }: InterviewPoolListProps) => {
  const { userGroup, isLoading } = useGetUserGroup({ id: value });
  const members = userGroup?.users ?? [];
  const [expanded, setExpanded] = useState(false);

  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (isLoading) {
    return <CenterSpinner height="40px" />;
  }

  if (members.length === 0) return null;

  const hasOverflow = members.length > INITIAL_VISIBLE;
  const visibleMembers = expanded ? members : members.slice(0, INITIAL_VISIBLE);
  const overflowCount = members.length - INITIAL_VISIBLE;

  return (
    <div className="mt-[-8px] flex flex-wrap items-center gap-2 rounded-md border border-secondary-100 bg-secondary-50 px-3 py-2">
      <Tags
        items={visibleMembers.map(toTagItem)}
        itemClassName="h-[30px] bg-white border border-secondary-100 py-0"
      />
      {hasOverflow && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="inline-flex cursor-pointer items-center rounded-sm border border-secondary-100 bg-white px-2 py-0 transition-colors hover:bg-secondary-50"
        >
          <Text variant="span" className="text-sm font-[550] text-primary-500">
            {expanded ? 'Show less' : `+${overflowCount} more`}
          </Text>
        </button>
      )}
    </div>
  );
};
